import { db } from "../firebase/config";
import { 
  collection, 
  getDocs, 
  query, 
  where, 
  doc, 
  getDoc,
  orderBy,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData
} from "firebase/firestore";
import { ExamResult, ExamAssignment, AssessmentTemplate, User } from "../types";

export interface EnhancedResult extends ExamResult {
  examName?: string;
  candidateName?: string;
  candidateEmail?: string;
  assignmentStatus?: string;
  orgName?: string;
}

export interface ResultFilters {
  candidateId?: string;
  examId?: string;
  assignmentId?: string;
  orgId?: string;
  status?: string;
  fromDate?: string;
  toDate?: string;
  minScore?: number;
  maxScore?: number;
}

export interface PaginatedResults {
  results: EnhancedResult[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
  total: number;
}

class ResultService {
  
  async getResultsByRole(user: User | null, filters?: ResultFilters): Promise<EnhancedResult[]> {
    if (!user) return [];
    
    try {
      let resultsData: ExamResult[] = [];
      
      if (filters?.assignmentId) {
        const resultRef = doc(db, "exam_results", filters.assignmentId);
        const resultSnap = await getDoc(resultRef);
        if (resultSnap.exists()) {
          resultsData = [{ id: resultSnap.id, ...resultSnap.data() } as ExamResult];
        }
      } 
      else if (filters?.candidateId) {
        resultsData = await this.getResultsByCandidate(filters.candidateId);
      }
      else if (user.role === 'super_admin') {
        resultsData = await this.getAllResults();
      }
      else if (user.role === 'org_admin' && user.orgId) {
        resultsData = await this.getResultsByOrg(user.orgId);
      }
      else if (user.role === 'candidate' && user.id) {
        resultsData = await this.getResultsByCandidate(user.id);
      }
      
      if (filters?.examId) {
        resultsData = resultsData.filter(r => r.examId === filters.examId);
      }
      
      if (filters?.fromDate) {
        resultsData = resultsData.filter(r => r.completedAt >= filters.fromDate!);
      }
      
      if (filters?.toDate) {
        resultsData = resultsData.filter(r => r.completedAt <= filters.toDate!);
      }
      
      if (filters?.minScore !== undefined) {
        resultsData = resultsData.filter(r => r.percentage >= filters.minScore!);
      }
      
      if (filters?.maxScore !== undefined) {
        resultsData = resultsData.filter(r => r.percentage <= filters.maxScore!);
      }
      
      return await this.enhanceResults(resultsData);
    } catch (error) {
      console.error("Error getting results by role:", error);
      return [];
    }
  }
  
  private async getAllResults(): Promise<ExamResult[]> {
    try {
      const resultsRef = collection(db, "exam_results");
      const snapshot = await getDocs(resultsRef);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ExamResult));
    } catch (error) {
      console.error("Error getting all results:", error);
      return [];
    }
  }
  
  private async getResultsByOrg(orgId: string): Promise<ExamResult[]> {
    try {
      const assignmentsRef = collection(db, "exam_assignments");
      const q = query(assignmentsRef, where("orgId", "==", orgId));
      const assignmentsSnap = await getDocs(q);
      const assignmentIds = assignmentsSnap.docs.map(doc => doc.id);
      
      if (assignmentIds.length === 0) return [];
      
      const resultsRef = collection(db, "exam_results");
      const resultsSnap = await getDocs(resultsRef);
      
      return resultsSnap.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as ExamResult))
        .filter(r => assignmentIds.includes(r.assignmentId));
    } catch (error) {
      console.error("Error getting results by org:", error);
      return [];
    }
  }
  
  private async getResultsByCandidate(candidateId: string): Promise<ExamResult[]> {
    try {
      const resultsRef = collection(db, "exam_results");
      const q = query(resultsRef, where("candidateId", "==", candidateId));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ExamResult));
    } catch (error) {
      console.error("Error getting results by candidate:", error);
      return [];
    }
  }
  
  private async enhanceResults(results: ExamResult[]): Promise<EnhancedResult[]> {
    const enhanced: EnhancedResult[] = [];
    
    for (const result of results) {
      let examName = result.examId;
      let candidateName = result.candidateId;
      let candidateEmail = '';
      let assignmentStatus = '';
      let orgName = '';
      
      try {
        const examRef = doc(db, "assessmentTemplates", result.examId);
        const examSnap = await getDoc(examRef);
        if (examSnap.exists()) {
          examName = examSnap.data().name;
          const orgId = examSnap.data().organizationId;
          if (orgId) {
            const orgRef = doc(db, "organizations", orgId);
            const orgSnap = await getDoc(orgRef);
            if (orgSnap.exists()) {
              orgName = orgSnap.data().name;
            }
          }
        }
      } catch (e) {}
      
      try {
        const userRef = doc(db, "users", result.candidateId);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          candidateName = userData.name || userData.displayName || result.candidateId;
          candidateEmail = userData.email || '';
        }
      } catch (e) {}
      
      try {
        const assignmentRef = doc(db, "exam_assignments", result.assignmentId);
        const assignmentSnap = await getDoc(assignmentRef);
        if (assignmentSnap.exists()) {
          assignmentStatus = assignmentSnap.data().status;
        }
      } catch (e) {}
      
      enhanced.push({
        ...result,
        examName,
        candidateName,
        candidateEmail,
        assignmentStatus,
        orgName
      });
    }
    
    return enhanced.sort((a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime());
  }
  
  async getResultStatistics(user: User | null, filters?: ResultFilters): Promise<{
    total: number;
    averageScore: number;
    passedCount: number;
    highScoreCount: number;
    lowScoreCount: number;
    passRate: number;
    averageTimeSpent: number;
    topPerformers: EnhancedResult[];
    recentResults: EnhancedResult[];
  }> {
    const results = await this.getResultsByRole(user, filters);
    
    const total = results.length;
    const averageScore = total > 0 ? Math.round(results.reduce((a, r) => a + r.percentage, 0) / total) : 0;
    const passedCount = results.filter(r => r.percentage >= 60).length;
    const highScoreCount = results.filter(r => r.percentage >= 80).length;
    const lowScoreCount = results.filter(r => r.percentage < 40).length;
    const passRate = total > 0 ? Math.round((passedCount / total) * 100) : 0;
    const averageTimeSpent = total > 0 ? Math.round(results.reduce((a, r) => a + (r.timeSpent || 0), 0) / total) : 0;
    
    const topPerformers = [...results]
      .sort((a, b) => b.percentage - a.percentage)
      .slice(0, 5);
    
    const recentResults = [...results]
      .sort((a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime())
      .slice(0, 10);
    
    return {
      total,
      averageScore,
      passedCount,
      highScoreCount,
      lowScoreCount,
      passRate,
      averageTimeSpent,
      topPerformers,
      recentResults
    };
  }
  
  async getPaginatedResults(
    user: User | null,
    filters?: ResultFilters,
    pageSize: number = 10,
    lastDoc?: QueryDocumentSnapshot<DocumentData>
  ): Promise<PaginatedResults> {
    if (!user) return { results: [], lastDoc: null, hasMore: false, total: 0 };
    
    try {
      let resultsQuery;
      const resultsRef = collection(db, "exam_results");
      
      if (filters?.candidateId) {
        resultsQuery = query(
          resultsRef,
          where("candidateId", "==", filters.candidateId),
          orderBy("completedAt", "desc"),
          limit(pageSize)
        );
      } 
      else if (user.role === 'candidate') {
        resultsQuery = query(
          resultsRef,
          where("candidateId", "==", user.id),
          orderBy("completedAt", "desc"),
          limit(pageSize)
        );
      }
      else {
        resultsQuery = query(
          resultsRef,
          orderBy("completedAt", "desc"),
          limit(pageSize)
        );
      }
      
      if (lastDoc) {
        resultsQuery = query(resultsQuery, startAfter(lastDoc));
      }
      
      const snapshot = await getDocs(resultsQuery);
      const results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ExamResult));
      const enhanced = await this.enhanceResults(results);
      
      let filtered = enhanced;
      if (filters?.examId) {
        filtered = filtered.filter(r => r.examId === filters.examId);
      }
      if (filters?.orgId && user.role === 'super_admin') {
        filtered = filtered.filter(r => r.orgName === filters.orgId);
      }
      
      const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;
      const hasMore = snapshot.docs.length === pageSize;
      
      const total = await this.getTotalCount(user, filters);
      
      return {
        results: filtered,
        lastDoc: lastVisible,
        hasMore,
        total
      };
    } catch (error) {
      console.error("Error getting paginated results:", error);
      return { results: [], lastDoc: null, hasMore: false, total: 0 };
    }
  }
  
  private async getTotalCount(user: User | null, filters?: ResultFilters): Promise<number> {
    const results = await this.getResultsByRole(user, filters);
    return results.length;
  }
  
  async getAvailableExams(): Promise<AssessmentTemplate[]> {
    try {
      const examsRef = collection(db, "assessmentTemplates");
      const snapshot = await getDocs(examsRef);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        ...doc.data()
      } as AssessmentTemplate));
    } catch (error) {
      console.error("Error getting available exams:", error);
      return [];
    }
  }
  
  async getResultById(resultId: string): Promise<EnhancedResult | null> {
    try {
      const resultRef = doc(db, "exam_results", resultId);
      const resultSnap = await getDoc(resultRef);
      
      if (!resultSnap.exists()) {
        return null;
      }
      
      const results = await this.enhanceResults([{ id: resultSnap.id, ...resultSnap.data() } as ExamResult]);
      return results[0] || null;
    } catch (error) {
      console.error("Error getting result by id:", error);
      return null;
    }
  }
  
  async downloadResultsCSV(user: User | null, filters?: ResultFilters): Promise<string> {
    const results = await this.getResultsByRole(user, filters);
    
    const headers = [
      'Candidate Name',
      'Candidate Email',
      'Exam Name',
      'Percentage Score',
      'Total Score',
      'Max Score',
      'Status',
      'Completed Date',
      'Traits'
    ];
    
    const rows = results.map(r => [
      r.candidateName,
      r.candidateEmail,
      r.examName,
      r.percentage,
      r.totalScore,
      r.maxScore,
      r.percentage >= 60 ? 'Passed' : 'Failed',
      new Date(r.completedAt).toLocaleString(),
      r.traitScores?.map(t => `${t.traitName}: ${t.percentage}%`).join('; ') || ''
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    return csvContent;
  }
}

export default new ResultService();